/**
 * Project API service.
 * Provides operations for managing projects and their associated GitHub repositories.
 *
 * API Endpoints:
 * - GET /projects - List user's projects
 * - POST /projects - Create a new project (and provisions a GitHub repo)
 * - DELETE /projects/:id - Delete a project
 *
 * @module services/projectService
 */

import { httpClient } from './httpClient';
import type { ProjectRequest, ProjectResponse } from '@/types';

const ENDPOINT = '/projects';

/**
 * Project Service
 * Encapsulates all project API operations
 */
export const projectService = {
  /**
   * List user's projects
   * @returns Promise<ProjectResponse[]> - Array of user's projects
   */
  async list(): Promise<ProjectResponse[]> {
    console.log('[ProjectService] Fetching user projects...');
    try {
      const response = await httpClient.get<ProjectResponse[]>(ENDPOINT);
      console.log('[ProjectService] Fetched', response.data.length, 'projects');
      return response.data;
    } catch (error) {
      console.error('[ProjectService] Failed to fetch projects:', error);
      throw error;
    }
  },

  /**
   * Create a new project (provisions a GitHub repo and stores project metadata)
   * @param request - The project creation request
   * @returns Promise<ProjectResponse> - The created project
   */
  async create(request: ProjectRequest): Promise<ProjectResponse> {
    console.log('[ProjectService] Creating project:', request.name);
    try {
      const response = await httpClient.post<ProjectResponse>(ENDPOINT, request);

      if (response.data && typeof response.data.id === 'string' && response.data.id.length > 0) {
        console.log('[ProjectService] Created project:', response.data.id);
        return response.data;
      }

      const locationHeader = response.headers?.location;
      if (locationHeader) {
        const getResponse = await httpClient.get<ProjectResponse>(locationHeader);
        return getResponse.data;
      }

      throw new Error('Project created but no response data or Location header received');
    } catch (error) {
      console.error('[ProjectService] Failed to create project:', error);
      throw error;
    }
  },

  /**
   * Delete a project (does not delete the underlying GitHub repo)
   * @param id - The project ID
   */
  async delete(id: string): Promise<void> {
    console.log('[ProjectService] Deleting project:', id);
    const encodedId = encodeURIComponent(id);
    try {
      await httpClient.delete(`${ENDPOINT}/${encodedId}`);
      console.log('[ProjectService] Deleted project:', id);
    } catch (error) {
      console.error('[ProjectService] Failed to delete project:', id, error);
      throw error;
    }
  },
};
